import { CircularProgress } from "@mui/material";
import { lazy } from "react";
import { shallow } from "zustand/shallow";

import logo from "./assets/images/logoNew.png";
import theme from "./theme.js";
import colors from "./_colors.scss";
import useGlobalState from "./use-global-state.js";

const SuspenseSpinner = () => (
	<div style={{ textAlign: "center", padding: "20px" }}>
		<CircularProgress size={50} />
	</div>
);

const PrimaryBackgroundButton = lazy(() =>
	import("tctt/Buttons").then((mod) => {
		const Component = mod.PrimaryBackgroundButton;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryBorderButton = lazy(() =>
	import("tctt/Buttons").then((mod) => {
		const Component = mod.PrimaryBorderButton;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const SecondaryBackgroundButton = lazy(() =>
	import("tctt/Buttons").then((mod) => {
		const Component = mod.SecondaryBackgroundButton;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const ThirdBackgroundButton = lazy(() =>
	import("tctt/Buttons").then((mod) => {
		const Component = mod.ThirdBackgroundButton;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Accordion = lazy(() =>
	import("tctt/Accordion").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryBackgroundAutocomplete = lazy(() =>
	import("tctt/Autocomplete").then((mod) => {
		const Component = mod.PrimaryBackgroundAutocomplete;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryBorderAutocomplete = lazy(() =>
	import("tctt/Autocomplete").then((mod) => {
		const Component = mod.PrimaryBorderAutocomplete;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Card = lazy(() =>
	import("tctt/Card").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryCheckbox = lazy(() =>
	import("tctt/Checkbox").then((mod) => {
		const Component = mod.PrimaryCheckbox;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const SecondaryCheckbox = lazy(() =>
	import("tctt/Checkbox").then((mod) => {
		const Component = mod.SecondaryCheckbox;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const ThirdCheckbox = lazy(() =>
	import("tctt/Checkbox").then((mod) => {
		const Component = mod.ThirdCheckbox;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryBackgroundCheckboxesDropdown = lazy(() =>
	import("tctt/CheckboxesDropdown").then((mod) => {
		const Component = mod.PrimaryBackgroundCheckboxesDropdown;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryBorderCheckboxesDropdown = lazy(() =>
	import("tctt/CheckboxesDropdown").then((mod) => {
		const Component = mod.PrimaryBorderCheckboxesDropdown;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryBackgroundDatepicker = lazy(() =>
	import("tctt/Datepicker").then((mod) => {
		const Component = mod.PrimaryBackgroundDatepicker;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryBorderDatepicker = lazy(() =>
	import("tctt/Datepicker").then((mod) => {
		const Component = mod.PrimaryBorderDatepicker;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Dialog = lazy(() =>
	import("tctt/Dialog").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryBackgroundDropdown = lazy(() =>
	import("tctt/Dropdowns").then((mod) => {
		const Component = mod.PrimaryBackgroundDropdown;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryBorderDropdown = lazy(() =>
	import("tctt/Dropdowns").then((mod) => {
		const Component = mod.PrimaryBorderDropdown;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const ErrorFallback = lazy(() =>
	import("tctt/ErrorFallback").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} logo={logo} customTheme={theme} customColors={colors} />
		};
	})
);

const Input = lazy(() =>
	import("tctt/Input").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Footer = lazy(() =>
	import("tctt/Footer").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Spinner = lazy(() =>
	import("tctt/Spinner").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Tooltip = lazy(() =>
	import("tctt/Tooltip").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimarySwitch = lazy(() =>
	import("tctt/Switches").then((mod) => {
		const Component = mod.PrimarySwitch;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const SecondarySwitch = lazy(() =>
	import("tctt/Switches").then((mod) => {
		const Component = mod.SecondarySwitch;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const ThirdSwitch = lazy(() =>
	import("tctt/Switches").then((mod) => {
		const Component = mod.ThirdSwitch;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimarySlider = lazy(() =>
	import("tctt/Slider").then((mod) => {
		const Component = mod.PrimarySlider;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const SecondarySlider = lazy(() =>
	import("tctt/Slider").then((mod) => {
		const Component = mod.SecondarySlider;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const ThirdSlider = lazy(() =>
	import("tctt/Slider").then((mod) => {
		const Component = mod.ThirdSlider;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Popup = lazy(() =>
	import("tctt/Popup").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Search = lazy(() =>
	import("tctt/Search").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Plot = lazy(() =>
	import("tctt/Plot").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const PrimaryRadioButtons = lazy(() =>
	import("tctt/RadioButtons").then((mod) => {
		const Component = mod.PrimaryRadioButtons;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const SecondaryRadioButtons = lazy(() =>
	import("tctt/RadioButtons").then((mod) => {
		const Component = mod.SecondaryRadioButtons;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const ThirdRadioButtons = lazy(() =>
	import("tctt/RadioButtons").then((mod) => {
		const Component = mod.ThirdRadioButtons;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Map = lazy(() =>
	import("tctt/Map").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Leaderboard = lazy(() =>
	import("tctt/Leaderboard").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Form = lazy(() =>
	import("tctt/Form").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const FileUpload = lazy(() =>
	import("tctt/FileUpload").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	})
);

const Table = lazy(() =>
	import("tctt/Table").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => {
				const { defaultPageSize, setDefaultPageSize } = useGlobalState(
					(e) => ({
						defaultPageSize: e.defaultPageSize,
						setDefaultPageSize: e.setDefaultPageSize,
					}),
					shallow
				);
				return <Component {...props} defaultPageSize={defaultPageSize} setDefaultPageSize={setDefaultPageSize} customTheme={theme} customColors={colors} />;
			}
		};
	})
);

const Header = lazy(() =>
	import("tctt/Header").then((mod) => {
		const Component = mod.default;
		return {
			default: (props) => <Component {...props} customTheme={theme} customColors={colors} />
		};
	}).catch(err => console.error("❌ failed to load Header:", err))
);

export {
	// Loading spinner until components are loaded
	SuspenseSpinner,

	// Buttons
	PrimaryBackgroundButton,
	PrimaryBorderButton,
	SecondaryBackgroundButton,
	ThirdBackgroundButton,

	// Accordion
	Accordion,

	// Autocomplete
	PrimaryBackgroundAutocomplete,
	PrimaryBorderAutocomplete,

	// Card
	Card,

	// Checkboxes
	PrimaryCheckbox,
	SecondaryCheckbox,
	ThirdCheckbox,

	// Dropdown with checkboxes
	PrimaryBackgroundCheckboxesDropdown,
	PrimaryBorderCheckboxesDropdown,

	// Datepickers
	PrimaryBackgroundDatepicker,
	PrimaryBorderDatepicker,

	// Dialog
	Dialog,

	// Dropdowns
	PrimaryBackgroundDropdown,
	PrimaryBorderDropdown,

	// Error fallback page
	ErrorFallback,

	// Input
	Input,

	// Footer
	Footer,

	// Spinner
	Spinner,

	// Tooltip
	Tooltip,

	// Switches
	PrimarySwitch,
	SecondarySwitch,
	ThirdSwitch,

	// Sliders
	PrimarySlider,
	SecondarySlider,
	ThirdSlider,

	// Popup
	Popup,

	// Search
	Search,

	// Plot
	Plot,

	// Radio Buttons
	PrimaryRadioButtons,
	SecondaryRadioButtons,
	ThirdRadioButtons,

	// Map
	Map,

	// Leaderboard
	Leaderboard,

	// Form
	Form,

	// File Upload
	FileUpload,

	// Table
	Table,

	// Header
	Header,
};